import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import React, { useEffect } from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";

interface Column {
    key?: string;
    label: string;
    group?: string;
    align?: string;
    width?: string;
    hideable?: boolean;
    hidden?: boolean;
    sortable?: boolean;
    isRowSpanColumn?: boolean;
    render?: (row: any, index: number) => React.ReactNode;
    labelRender?: () => React.ReactNode;
    padding?: string;
}

interface PaginationProps {
    currentPage: number;
    totalCount: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
}

interface CommonTableProps {
    columns: Column[];
    data: any[];
    pagination?: PaginationProps;
    tableClassName?: string;
    emptyMessage?: string;
    showColumnManager?: boolean;
    storageKey?: string;
    columnClassName?: string
    sortKey?: string | null;
    sortOrder?: "asc" | "desc" | null;
    onSortChange?: (sortKey: string, sortOrder: "asc" | "desc" | null) => void;
}

const CustomDynamicTable: React.FC<CommonTableProps> = ({
    columns,
    data,
    pagination,
    tableClassName,
    columnClassName,
    emptyMessage = "No data available",
    showColumnManager = false,
    storageKey = "dynamic-table-columns",
    sortKey = null,
    sortOrder = null,
    onSortChange,
}) => {

    const initializeColumnState = () => {
        // If column manager is OFF → ignore localStorage
        if (!showColumnManager) {
            return columns.map(col => ({
                ...col,
                hideable: col.hideable ?? true,
                hidden: col.hidden ?? false,
            }));
        }
        // If column manager is ON → use localStorage
        const saved = localStorage.getItem(storageKey);

        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return columns.map((col, index) => ({
                    ...col,
                    hidden: parsed[index]?.hidden ?? col.hidden ?? false,
                    hideable: col.hideable ?? true
                }));
            } catch {
                return columns.map(col => ({
                    ...col,
                    hideable: col.hideable ?? true,
                    hidden: col.hidden ?? false,
                }));
            }
        }

        return columns.map(col => ({
            ...col,
            hideable: col.hideable ?? true,
            hidden: col.hidden ?? false,
        }));
    };

    const [columnState, setColumnState] = React.useState(initializeColumnState);

    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify(columnState));
    }, [columnState]);

    const visibleColumns = columnState.filter(c => !c.hidden);

    const toggleColumn = (index: number) => {
        const updated = [...columnState];

        if (updated[index].hideable !== false) {
            updated[index].hidden = !updated[index].hidden;
            setColumnState(updated);
        }
    };

    const getAlignClass = (align?: string) => {
        if (align === "center") return "text-center";
        if (align === "right") return "text-end";
        return "";
    };

    const handleSort = (col: Column) => {
        if (!col.key || !col.sortable || !onSortChange) return;

        let next: "asc" | "desc" | null = "asc";

        if (sortKey === col.key && sortOrder === "asc") next = "desc";
        else if (sortKey === col.key && sortOrder === "desc") next = "asc";

        onSortChange(col.key, next);
    };

    const processRowSpan = (rows: any[], columns: any[]) => {
        const map: any = {};

        columns.forEach((col: any) => {
            if (col.isRowSpanColumn && col.key) {
                let prev: any = null;
                let count = 0;

                rows.forEach((row, i) => {
                    const value = row[col.key];

                    if (value === prev) {
                        map[`${i}-${col.key}`] = 0;
                        count++;
                    } else {
                        count = 1;
                    }

                    const nextValue = rows[i + 1]?.[col.key];

                    if (value !== nextValue) {
                        map[`${i - count + 1}-${col.key}`] = count;
                    }

                    prev = value;
                });
            }
        });

        return map;
    };

    const rowSpanMap = processRowSpan(data, columnState);

    const renderPagination = () => {
        if (!pagination) return null;

        const { currentPage, totalCount, pageSize, onPageChange, onPageSizeChange } = pagination;
        const totalPages = Math.ceil(totalCount / pageSize);

        return (
            <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">

                <div className="d-flex align-items-center gap-2">
                    <label className="small text-muted mb-0">Rows per page:</label>
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
                        className="form-select form-select-sm w-auto"
                    >
                        {[5, 10, 25, 50, 100].map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </div>

                <div className="text-muted medium">
                    Showing page {currentPage} of {totalPages} ({totalCount} records)
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn btn-sm btn-light"
                        disabled={currentPage === 1}
                        onClick={() => onPageChange(currentPage - 1)}
                    >
                        Previous
                    </button>
                    <button
                        className="btn btn-sm btn-light"
                        disabled={currentPage === totalPages}
                        onClick={() => onPageChange(currentPage + 1)}
                    >
                        Next
                    </button>
                </div>

            </div>
        );
    };

    return (
        <div>

            {showColumnManager && (
                <div className="d-flex justify-content-end">
                    <Dropdown as={ButtonGroup} className="mb-3" autoClose="outside" align="end">
                        <Dropdown.Toggle variant="light">
                            Columns
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                            className="p-3"
                            style={{ minWidth: "250px", maxHeight: "300px", overflowY: "auto" }}
                            popperConfig={{ strategy: "fixed" }}
                        >
                            {columnState.map((col, i) => (
                                <div key={i} className="my-3" onClick={(e) => e.stopPropagation()}>
                                    <label className="fw-normal fs-16 cursor-pointer d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input cursor-pointer"
                                            checked={!col.hidden}
                                            disabled={col.hideable === false}
                                            onChange={() => toggleColumn(i)}
                                            style={{ height: "1.5rem", width: "1.5rem" }}
                                        />
                                        <span>
                                            {col.label}
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            )}

            <div className="table-responsive">

            <div style={{ maxHeight: "75vh" }} className="overflow-y-auto">
                <table className={tableClassName || "table table-bordered align-middle mx-auto w-100"}>
                    <thead>
                        <tr className="bg-secondary align-middle">
                            {visibleColumns.map((col, i) => {
                                if (!col.group) {
                                    return (
                                        <th
                                            key={i}
                                            rowSpan={2}
                                            style={col.width ? { width: col.width } : undefined}
                                            className={`${getAlignClass(col.align)} text-gray-700 cursor-pointer border border-gray-300 ${columnClassName || ''}`}
                                            onClick={() => handleSort(col)}
                                        >
                                            <div className={`d-flex align-items-center gap-2
              ${col.align === "center" ? "justify-content-center" : ""}
              ${col.align === "right" ? "justify-content-end" : ""}
            `}>
                                                {col.labelRender ? col.labelRender() : col.label}

                                                {col.sortable && (
                                                    <>
                                                        {sortKey === col.key && sortOrder === "asc" && <ArrowUp size={16} />}
                                                        {sortKey === col.key && sortOrder === "desc" && <ArrowDown size={16} />}
                                                        {sortKey !== col.key && <ArrowUpDown size={16} />}
                                                    </>
                                                )}
                                            </div>
                                        </th>
                                    );
                                }

                                const groupCols = visibleColumns.filter(x => x.group === col.group);
                                if (groupCols[0] !== col) return null;

                                return (
                                    <th
                                        key={i}
                                        colSpan={groupCols.length}
                                        className="text-center fw-bold text-gray-700 border border-gray-300"
                                    >
                                        {col.group}
                                    </th>
                                );
                            })}
                        </tr>

                        <tr className="bg-secondary align-middle">
                            {visibleColumns
                                .filter(col => col.group)
                                .map((col, i) => (
                                    <th
                                        key={i}
                                        style={col.width ? { width: col.width } : undefined}
                                        className={`${getAlignClass(col.align)} text-gray-700 cursor-pointer border border-gray-300`}
                                        onClick={() => handleSort(col)}
                                    >
                                        <div className={`d-flex align-items-center gap-2
            ${col.align === "center" ? "justify-content-center" : ""}
            ${col.align === "right" ? "justify-content-end" : ""}
          `}>
                                            {col.label}

                                            {col.sortable && (
                                                <>
                                                    {sortKey === col.key && sortOrder === "asc" && <ArrowUp size={16} />}
                                                    {sortKey === col.key && sortOrder === "desc" && <ArrowDown size={16} />}
                                                    {sortKey !== col.key && <ArrowUpDown size={16} />}
                                                </>
                                            )}
                                        </div>
                                    </th>
                                ))}
                        </tr>

                    </thead>

                    <tbody className="align-middle">
                        {data.length > 0 ? (
                            data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {visibleColumns.map((col, colIndex) => (
                                        <td
                                            key={colIndex}
                                            rowSpan={rowSpanMap[`${rowIndex}-${col.key}`] || undefined}
                                            style={{
                                                padding: col.padding || "0.75rem 0.75rem",
                                                width: col.width,
                                                display:
                                                    rowSpanMap[`${rowIndex}-${col.key}`] === 0
                                                        ? "none"
                                                        : undefined,
                                                verticalAlign:
                                                    rowSpanMap[`${rowIndex}-${col.key}`] > 1
                                                        ? "middle"
                                                        : undefined
                                            }}
                                            className={`${col.align === "center"
                                                ? "text-center"
                                                : col.align === "right"
                                                    ? "text-end"
                                                    : ""}`}
                                        >
                                            {col.render ? col.render(row, rowIndex) : col.key ? row[col.key] : null}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={visibleColumns.length} className="text-center py-4">
                                    {emptyMessage}
                                </td>
                            </tr>
                        )}
                    </tbody>

                </table>
            </div>
            </div>

            {renderPagination()}
        </div>
    );
};

export default CustomDynamicTable;
